import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useMatch, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LuLayoutList, LuSettings } from 'react-icons/lu';
import { API_BASE_URL } from '../../config.js';
import { compareReportsDesc } from '../../utils/weekFormat.js';
import { sourceLabel } from '../../utils/sources.js';
import CardSelect from '../ui/CardSelect';
import AdminBadge from '../ui/AdminBadge';
import ErrorBoundary from '../ui/ErrorBoundary';
import { ErrorMessage } from '../ui/ErrorMessage';
import { ViewerMobileNav, ViewerSidebar } from '../ui/ViewerSidebar';
import { DEFAULT_REPORT_TAB, getReportTabs, reportTabHref } from './reportTabs.js';
import { jailParam, withJail } from './jail.js';

const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

/**
 * Layout of /w/:slug and /w/:slug/:id: client switcher + navigation on the left, the list or
 * the report on the right. Loads the client and its weeks once and shares them (Outlet context)
 * so moving between reports never reloads the sidebar.
 */
const ClientViewer = ({ fetchWithAuth, currentUser }) => {
  const { slug } = useParams();
  const reportMatch = useMatch('/w/:slug/:id');
  const reportId = reportMatch?.params?.id || null;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const scrollRef = useRef(null);
  // Read once on arrival: a jailed viewer stays jailed while it is mounted, and every internal
  // link carries the param on so a reload does too.
  const jailRef = useRef(jailParam(searchParams));
  const jail = jailRef.current;

  const [myConfigs, setMyConfigs] = useState([]);
  const [client, setClient] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // { reportId, analyses, pictures }: published by the open report, null while unknown.
  const [reportCounts, setReportCounts] = useState(null);

  const isAdminUser = !!(currentUser?.isadmin || currentUser?.issuperadmin);

  // The other clients only feed the picker, which a jailed viewer does not show.
  useEffect(() => {
    if (jail) return undefined;
    let cancelled = false;
    fetchWithAuth(`${API_BASE_URL}/api/weekly/my`)
      .then((response) => (response.ok ? readJson(response) : []))
      .then((data) => { if (!cancelled) setMyConfigs(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setMyConfigs([]); });
    return () => { cancelled = true; };
  }, [fetchWithAuth, jail]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setClient(null);
      setWeeks([]);
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/weekly/client/${slug}`);
        const data = await readJson(response);
        if (cancelled) return;
        if (data?.errorType === 'REPORT_ACCESS_DENIED') {
          setError('Vous n\'avez pas accès à ce rapport.');
          return;
        }
        if (!response.ok || !data?.client) {
          setError('Client introuvable ou erreur lors du chargement des données.');
          return;
        }
        const weeksResponse = await fetchWithAuth(`${API_BASE_URL}/api/weekly/client/${slug}/weeks`);
        const weeksData = await readJson(weeksResponse);
        // Switching client while this one loads: its weeks must not land under the next.
        if (cancelled) return;
        setClient(data.client);
        setWeeks(Array.isArray(weeksData) ? [...weeksData].sort(compareReportsDesc) : []);
      } catch (err) {
        console.error('Erreur lors du chargement du client:', err);
        if (!cancelled) setError('Client introuvable ou erreur lors du chargement des données.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fetchWithAuth, slug]);

  // A new report or section starts at the top: the content column scrolls, not the window.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [pathname, search]);

  const upsertWeek = useCallback((report) => {
    if (!report?.id) return;
    setWeeks((prev) => {
      const exists = prev.some((w) => w.id === report.id);
      const next = exists ? prev.map((w) => (w.id === report.id ? { ...w, ...report } : w)) : [...prev, report];
      return next.sort(compareReportsDesc);
    });
  }, []);

  // After a generation: the server decides which report it created or reset.
  const reloadWeeks = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/weekly/client/${slug}/weeks`);
      const data = await readJson(response);
      if (response.ok && Array.isArray(data)) setWeeks([...data].sort(compareReportsDesc));
    } catch (err) {
      console.error('Erreur lors du rechargement des rapports:', err);
    }
  }, [fetchWithAuth, slug]);

  const removeWeek = useCallback((id) => setWeeks((prev) => prev.filter((w) => w.id !== id)), []);

  const clientOptions = useMemo(
    () => myConfigs.map((c) => ({ value: c.slug, label: c.name, subtitle: sourceLabel(c.source_type) })),
    [myConfigs],
  );
  const selectedOption = clientOptions.find((o) => o.value === slug)
    || (client ? { value: client.slug, label: client.name, subtitle: sourceLabel(client.source_type) } : null);

  const currentReport = reportId ? weeks.find((w) => w.id === reportId) : null;
  const reportTabs = getReportTabs(isAdminUser);
  // Same fallback as the report page: an unknown or forbidden ?tab shows the analyses.
  const requestedTab = searchParams.get('tab');
  const activeTab = reportTabs.some((t) => t.id === requestedTab) ? requestedTab : DEFAULT_REPORT_TAB;
  const counts = reportCounts && reportCounts.reportId === reportId ? reportCounts : null;

  // Jailed viewers keep the way back to their client's list: without it a report is a dead end.
  const sections = [
    {
      id: 'client',
      items: [{
        id: 'reports',
        label: 'Rapports',
        icon: LuLayoutList,
        to: withJail(`/w/${slug}`, jail),
        active: !reportId,
        count: weeks.length > 0 ? weeks.length : null,
      }],
    },
    reportId && {
      id: 'report',
      label: currentReport ? `Semaine ${currentReport.week_number} · ${currentReport.year}` : 'Rapport',
      items: reportTabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        icon: tab.icon,
        to: withJail(reportTabHref(slug, reportId, tab.id), jail),
        active: activeTab === tab.id,
        count: tab.count ? counts?.[tab.count] ?? null : null,
        badge: tab.admin ? <AdminBadge /> : null,
      })),
    },
  ].filter(Boolean);

  const clientSelect = (
    <CardSelect
      label="Client"
      value={selectedOption}
      onChange={(opt) => opt.value !== slug && navigate(`/w/${opt.value}`)}
      options={clientOptions}
      placeholder="Choisir un client…"
      searchPlaceholder="Rechercher un client…"
      sectionLabel="Vos rapports"
    />
  );

  // Admin shortcut out of the viewer: not offered to a jailed session.
  const configLink = !jail && currentUser?.issuperadmin && client && (
    <Link
      to={`/admin/configs?configId=${client.id}`}
      className="flex items-center gap-2 rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs font-medium text-gray-500 outline-none hover:border-gray-300 hover:text-gray-800 focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
    >
      <LuSettings className="h-4 w-4" aria-hidden />
      <span className="flex-1">Configuration du client</span>
      <AdminBadge />
    </Link>
  );

  const context = {
    client,
    weeks,
    loading,
    isAdminUser,
    upsertWeek,
    reloadWeeks,
    removeWeek,
    setReportCounts,
    jail,
  };

  return (
    <div className="flex h-full overflow-hidden">
      <ViewerSidebar
        header={jail ? null : clientSelect}
        sections={sections}
        footer={configLink}
        navLabel="Navigation du rapport"
        collapsible
        storageKey="weekly.sidebar.collapsed"
      />

      <div ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:px-6 sm:py-6">
          <ViewerMobileNav header={jail ? null : clientSelect} sections={sections} navLabel="Navigation du rapport" />
          {error ? (
            <ErrorMessage message={error} title="Rapport indisponible" showHomeLink={!jail} />
          ) : (
            <ErrorBoundary resetKeys={[pathname]}>
              <Outlet context={context} />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientViewer;
